import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { gl } from '../../Engine';
import { BoundingBoxBounds } from '~/engine/data/common/BoundingBox';

/**
 * {@link SignedDistanceField} is just a 3D {@link WebGLTexture texture}.
 */
export class SignedDistanceField {
    /**
     * {@link WebGLTexture}-object that stores the textureData on the GPU.
     */
    private _texture: WebGLTexture;

    /**
     * Current {@link vec3 size} of this {@link SignedDistanceField} and it's {@link _texture texture} in pixels.
     */
    private _size: vec3;

    /**
     * Cached pixel data for CPU calculations.
     */
    private _cachedPixels: Uint8Array[] | undefined;

    /**
     * Monotonically increasing counter incremented whenever
     * pixel data changes. Consumers can compare against a
     * stored version to detect staleness.
     */
    private _dataVersion = 0;

    public get dataVersion(): number {
        return this._dataVersion;
    }

    public constructor(size: ReadonlyVec3) {
        this._size = vec3.create();
        this._cachedPixels = undefined;
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error(`Texture creation for SDF failed`);
        }
        this._texture = texture;
        this.resize(size);
    }

    /**
     * Method to get the {@link _size size} of this {@link SignedDistanceField} in pixels.
     */
    public get size(): ReadonlyVec3 {
        return this._size;
    }

    /**
     * Method to get reference to the {@link WebGLTexture} stored within this {@link SignedDistanceField}.
     */
    public get texture(): WebGLTexture {
        return this._texture;
    }

    /**
     * Get cached pixel data, reading from GPU if not cached.
     */
    public get pixels(): Uint8Array[] {
        if (!this._cachedPixels) {
            this._cachedPixels = this.toArray();
        }
        return this._cachedPixels;
    }

    /**
     * Invalidate the cached pixel data. Call this when the texture is modified.
     */
    public invalidateCachedPixels(): void {
        this._dataVersion++;
        this._cachedPixels = undefined;
    }

    /**
     * Set the cached pixel data directly, avoiding a GPU readback
     * when the CPU-side data is already known (e.g. right after
     * uploading via {@link arrayToTexture}).
     */
    public setCachedPixels(pixels: Uint8Array[]): void {
        this._cachedPixels = pixels;
    }

    /** Whether the CPU pixel cache is populated. */
    public get hasCachedPixels(): boolean {
        return this._cachedPixels !== undefined;
    }

    /**
     * Update the CPU cache for a single slice without
     * invalidating the rest. No-op if the cache is not
     * populated.
     *
     * For transversal (axis 2), replaces the Z-slice directly.
     * For coronal/sagittal (axis 0/1), scatters the 2D slice
     * data across per-Z-slice cache arrays.
     */
    public updateCachedSlice(
        sliceIdx: number,
        axis: 0 | 1 | 2,
        data: Uint8Array
    ): void {
        if (!this._cachedPixels) {
            return;
        }

        this._dataVersion++;

        if (axis === 2) {
            this._cachedPixels[sliceIdx] = data;
            return;
        }

        const sizeX = this._size[0];
        const sizeY = this._size[1];
        const sizeZ = this._size[2];
        // Coronal: sizeX wide, sizeZ tall.
        // Sagittal: sizeY wide, sizeZ tall.
        const rowWidth = axis === 1 ? sizeX : sizeY;

        for (let z = 0; z < sizeZ; z++) {
            const cached = this._cachedPixels[z];
            if (!cached) {
                continue;
            }
            const rowOffset = z * rowWidth;

            if (axis === 1) {
                const cacheRow = sliceIdx * sizeX;
                for (let x = 0; x < rowWidth; x++) {
                    cached[cacheRow + x] = data[rowOffset + x];
                }
            } else {
                for (let y = 0; y < rowWidth; y++) {
                    cached[y * sizeX + sliceIdx] = data[rowOffset + y];
                }
            }
        }
    }

    /**
     * Check whether any pixel value exceeds the given threshold.
     * Scans from the center slice outward so that structures
     * surrounded by empty margin are found quickly.
     */
    public hasPixelsAbove(threshold: number): boolean {
        return SignedDistanceField.hasPixelsAboveThreshold(
            this.pixels,
            this._size[2],
            threshold
        );
    }

    /**
     * Pure function to check whether any pixel value exceeds the
     * given threshold. Scans from the center slice outward so that
     * structures surrounded by empty margin are found quickly.
     *
     * @param pixelData Array of Uint8Array slices (one per Z level)
     * @param sizeZ Number of slices
     * @param threshold Pixel value threshold
     */
    public static hasPixelsAboveThreshold(
        pixelData: Uint8Array[],
        sizeZ: number,
        threshold: number
    ): boolean {
        const midZ = sizeZ >> 1;

        for (let r = 0; r < sizeZ; r++) {
            const zUp = midZ + r;
            if (zUp < sizeZ) {
                const slice = pixelData[zUp];
                for (let i = 0; i < slice.length; i++) {
                    if (slice[i] > threshold) {
                        return true;
                    }
                }
            }
            // Skip r === 0 to avoid scanning the middle twice
            if (r > 0) {
                const zDown = midZ - r;
                if (zDown >= 0) {
                    const slice = pixelData[zDown];
                    for (let i = 0; i < slice.length; i++) {
                        if (slice[i] > threshold) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    /**
     * Calculate the bounding box of content (pixels > threshold) in pixel coordinates.
     * Uses an optimized edge-scanning algorithm that skips interior pixels.
     *
     * @param threshold Pixel value threshold (default 0, meaning any non-zero pixel)
     * @returns BoundingBoxBounds with min/max pixel coordinates, or undefined if no content found
     */
    public getContentBounds(
        threshold: number = 0
    ): BoundingBoxBounds | undefined {
        return SignedDistanceField.computeContentBounds(
            this.pixels,
            this._size[0],
            this._size[1],
            this._size[2],
            threshold
        );
    }

    /**
     * Pure function to calculate content bounds from pixel data.
     * Uses an optimized edge-scanning algorithm that skips interior pixels.
     *
     * @param pixelData Array of Uint8Array slices (one per Z level)
     * @param sizeX Width in pixels
     * @param sizeY Height in pixels
     * @param sizeZ Depth in slices
     * @param threshold Pixel value threshold (default 0)
     * @returns BoundingBoxBounds with min/max pixel coordinates, or undefined if no content found
     */
    public static computeContentBounds(
        pixelData: Uint8Array[],
        sizeX: number,
        sizeY: number,
        sizeZ: number,
        threshold: number = 0
    ): BoundingBoxBounds | undefined {
        if (sizeX === 0 || sizeY === 0 || sizeZ === 0) {
            return undefined;
        }

        // Helper to check if a row has any content
        const rowHasContent = (slice: Uint8Array, y: number) => {
            const rowStart = y * sizeX;
            for (let x = 0; x < sizeX; x++) {
                if (slice[rowStart + x] > threshold) {
                    return true;
                }
            }
            return false;
        };

        // Helper to check if a pixel has content
        const hasContentAt = (slice: Uint8Array, x: number, y: number) =>
            slice[y * sizeX + x] > threshold;

        // Find Z bounds by scanning slices from edges
        let minZ = -1,
            maxZ = -1;
        for (let z = 0; z < sizeZ && minZ < 0; z++) {
            for (let i = 0; i < pixelData[z].length; i++) {
                if (pixelData[z][i] > threshold) {
                    minZ = z;
                    break;
                }
            }
        }
        if (minZ < 0) {
            return undefined;
        }
        for (let z = sizeZ - 1; z >= minZ && maxZ < 0; z--) {
            for (let i = 0; i < pixelData[z].length; i++) {
                if (pixelData[z][i] > threshold) {
                    maxZ = z;
                    break;
                }
            }
        }

        // Find Y bounds within Z range, scanning from edges
        let minY = sizeY,
            maxY = -1;
        for (let z = minZ; z <= maxZ; z++) {
            const slice = pixelData[z];
            for (let y = 0; y < minY; y++) {
                if (rowHasContent(slice, y)) {
                    minY = y;
                    break;
                }
            }
            for (let y = sizeY - 1; y > maxY; y--) {
                if (rowHasContent(slice, y)) {
                    maxY = y;
                    break;
                }
            }
        }

        // Find X bounds within Z and Y range, scanning from edges
        let minX = sizeX,
            maxX = -1;
        for (let z = minZ; z <= maxZ; z++) {
            const slice = pixelData[z];
            for (let y = minY; y <= maxY; y++) {
                for (let x = 0; x < minX; x++) {
                    if (hasContentAt(slice, x, y)) {
                        minX = x;
                        break;
                    }
                }
                for (let x = sizeX - 1; x > maxX; x--) {
                    if (hasContentAt(slice, x, y)) {
                        maxX = x;
                        break;
                    }
                }
            }
        }

        return {
            min: vec3.fromValues(minX, minY, minZ),
            max: vec3.fromValues(maxX, maxY, maxZ),
        };
    }

    /**
     * Method to resize this {@link SignedDistanceField}.\
     * This checks if the old {@link _size size} equals the {@link size}. If it does, returns false.\
     * Otherwise the {@link _texture texture} is resized and True is returned.
     * @param size The new {@link _size size} that the {@link SignedDistanceField} should have in pixels.
     * @returns Returns true if the {@link SignedDistanceField} was resized,\
     * false if {@link SignedDistanceField}'s {@link _size size} was already equal to {@link size}, in which case resize was not needed.
     */
    public resize(size: ReadonlyVec3): boolean {
        if (vec3.equals(this._size, size)) {
            return false;
        }
        this._dataVersion++;
        vec3.copy(this._size, size);
        this._cachedPixels = undefined;
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, this._texture);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, 0);
        gl.texParameteri(
            gl.TEXTURE_3D,
            gl.TEXTURE_MIN_FILTER,
            gl.LINEAR_MIPMAP_LINEAR
        );
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        gl.texImage3D(
            gl.TEXTURE_3D,
            0, // level
            gl.R8, // internalformat
            size[0], // width
            size[1], // height
            size[2], // depth
            0,
            gl.RED, // format
            gl.UNSIGNED_BYTE, // type
            null // pixel
        );
        return true;
    }

    /**
     * Method to get the SDF texture values from WebGLTexture as an array.
     * Reads directly via gl.RED (1 byte/pixel) for efficiency with R8 textures.
     */
    public toArray(): Uint8Array[] {
        const sizeX = this._size[0];
        const sizeY = this._size[1];
        const sizeZ = this._size[2];
        if (sizeX === 0 || sizeY === 0 || sizeZ === 0) {
            return [];
        }

        // Save caller's FBO — toArray may be invoked mid-render
        // (e.g. via cropToImageBounds during lazy texture creation)
        const callerFbo = gl.getParameter(
            gl.FRAMEBUFFER_BINDING
        ) as WebGLFramebuffer | null;

        const xySize = sizeX * sizeY;
        const result: Uint8Array[] = new Array(sizeZ);
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        // R8 rows may not be a multiple of 4 bytes; disable row padding
        gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
        for (let z = 0; z < sizeZ; z++) {
            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                this._texture,
                0,
                z
            );
            const slice = new Uint8Array(xySize);
            gl.readPixels(0, 0, sizeX, sizeY, gl.RED, gl.UNSIGNED_BYTE, slice);
            result[z] = slice;
        }
        gl.pixelStorei(gl.PACK_ALIGNMENT, 4); // Reset to WebGL default
        gl.bindFramebuffer(gl.FRAMEBUFFER, callerFbo);
        gl.deleteFramebuffer(fb);
        return result;
    }

    // get the SDF distance value at a given point, this indicates the distance to the edge of the contour
    // distance is not in physical units, but as the distance field value
    public distanceToContour(point: ReadonlyVec3): number {
        const sizeX = this._size[0];
        const sizeY = this._size[1];
        const sizeZ = this._size[2];

        if (
            point[0] < 0 ||
            point[0] >= sizeX ||
            point[1] < 0 ||
            point[1] >= sizeY ||
            point[2] < 0 ||
            point[2] >= sizeZ
        ) {
            return 9999;
        }

        // Use nearest-neighbor in Z with bilinear XY interpolation
        // from cached pixels. Trilinear Z blending (matching GL_LINEAR)
        // creates phantom contour edges between cleared and filled
        // slices — a cleared slice (all 0) next to a filled interior
        // slice (all 255) interpolates to ~127.5 at the SDF threshold,
        // producing false contour hits. Nearest Z avoids this while
        // bilinear XY preserves smooth in-plane distance queries.
        const z = Math.max(0, Math.min(Math.floor(point[2]), sizeZ - 1));
        const slice = this.pixels[z];

        const x0 = Math.max(0, Math.floor(point[0]));
        const y0 = Math.max(0, Math.floor(point[1]));
        const x1 = Math.min(x0 + 1, sizeX - 1);
        const y1 = Math.min(y0 + 1, sizeY - 1);
        const dx = point[0] - x0;
        const dy = point[1] - y0;

        const v00 = slice[y0 * sizeX + x0];
        const v10 = slice[y0 * sizeX + x1];
        const v01 = slice[y1 * sizeX + x0];
        const v11 = slice[y1 * sizeX + x1];

        const v =
            v00 * (1 - dx) * (1 - dy) +
            v10 * dx * (1 - dy) +
            v01 * (1 - dx) * dy +
            v11 * dx * dy;

        return Math.abs(v - 127.5);
    }

    public sliceToArray(sliceIdx: number): Uint8Array {
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        const result = this.sliceToArrayInternal(sliceIdx);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
        return result;
    }

    /**
     * Read back a single 2D slice from the GPU texture along
     * any axis and update the CPU pixel cache with the result.
     * No-op if the cache is not populated.
     *
     * For axis 2 (transversal): reads one Z-layer.
     * For axis 1 (coronal): reads one row per Z-layer.
     * For axis 0 (sagittal): reads one column per Z-layer.
     */
    public syncSliceFromGpu(sliceIdx: number, axis: 0 | 1 | 2): void {
        if (!this._cachedPixels) {
            return;
        }

        const sizeX = this._size[0];
        const sizeY = this._size[1];
        const sizeZ = this._size[2];

        if (axis === 2) {
            this._cachedPixels[sliceIdx] = this.sliceToArray(sliceIdx);
            this._dataVersion++;
            return;
        }

        // Coronal / sagittal: read the relevant row or column
        // from each Z-layer and assemble into the 2D slice.
        const rowWidth = axis === 1 ? sizeX : sizeY;
        const sliceData = new Uint8Array(rowWidth * sizeZ);

        const callerFbo = gl.getParameter(
            gl.FRAMEBUFFER_BINDING
        ) as WebGLFramebuffer | null;

        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.pixelStorei(gl.PACK_ALIGNMENT, 1);

        for (let z = 0; z < sizeZ; z++) {
            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                this._texture,
                0,
                z
            );
            if (axis === 1) {
                // Coronal: read row at y=sliceIdx
                gl.readPixels(
                    0,
                    sliceIdx,
                    sizeX,
                    1,
                    gl.RED,
                    gl.UNSIGNED_BYTE,
                    sliceData.subarray(z * rowWidth, (z + 1) * rowWidth)
                );
            } else {
                // Sagittal: read column at x=sliceIdx
                gl.readPixels(
                    sliceIdx,
                    0,
                    1,
                    sizeY,
                    gl.RED,
                    gl.UNSIGNED_BYTE,
                    sliceData.subarray(z * rowWidth, (z + 1) * rowWidth)
                );
            }
        }

        gl.pixelStorei(gl.PACK_ALIGNMENT, 4);
        gl.bindFramebuffer(gl.FRAMEBUFFER, callerFbo);
        gl.deleteFramebuffer(fb);

        this.updateCachedSlice(sliceIdx, axis, sliceData);
    }

    private sliceToArrayInternal(sliceIdx: number): Uint8Array {
        const xySize = this._size[0] * this._size[1];
        const sliceArray = new Uint8Array(xySize);

        gl.framebufferTextureLayer(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            this._texture,
            0,
            sliceIdx
        );

        // Read R8 texture directly via gl.RED (1 byte/pixel),
        // matching the strategy used in toArray().
        gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
        gl.readPixels(
            0,
            0,
            this.size[0],
            this.size[1],
            gl.RED,
            gl.UNSIGNED_BYTE,
            sliceArray
        );
        gl.pixelStorei(gl.PACK_ALIGNMENT, 4);
        return sliceArray;
    }

    public arrayToTextureSlice(array: Uint8Array, sliceIdx: number): void {
        this._dataVersion++;
        if (this._cachedPixels) {
            this._cachedPixels[sliceIdx] = array;
        }
        if (array.length !== this.size[0] * this.size[1]) {
            throw new Error(
                `Array length ${array.length} does not match texture size ${this.size[0]} x ${this.size[1]}`
            );
        }
        gl.bindTexture(gl.TEXTURE_3D, this._texture);
        // R8 rows may not be a multiple of 4 bytes; disable row padding
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            0,
            0,
            sliceIdx,
            this.size[0],
            this.size[1],
            1,
            gl.RED,
            gl.UNSIGNED_BYTE,
            array
        );
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4); // Reset to WebGL default
        gl.bindTexture(gl.TEXTURE_3D, null);
    }

    public arrayToTexture(array: Uint8Array[], sliceOffset: number = 0): void {
        for (let z = 0; z < array.length; ++z) {
            this.arrayToTextureSlice(array[z], z + sliceOffset);
        }
    }

    /**
     * Method to {@link gl.bindTexture bind} the {@link _texture texture} to {@link gl.TEXTURE_3D TEXTURE_3D} target.
     * @returns True to indicate that the {@link _texture texture} was successfully bound.
     */
    public bind(): boolean {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, this._texture);
        return true;
    }

    /**
     * Method to unbind the texture. Will unbind any texture that has been bound.
     */
    public unbind() {
        gl.bindTexture(gl.TEXTURE_3D, null);
    }

    /**
     * Method to delete this {@link SignedDistanceField}.\
     * This deletes the {@link _texture texture}, de-allocating the WebGL memory consumed by this {@link SignedDistanceField}.
     * @returns True to indicate that the {@link SignedDistanceField} was successfully deleted.
     */
    public delete() {
        gl.deleteTexture(this._texture);
    }

    /**
     * Method to clear the {@link _texture texture} of this {@link SignedDistanceField}.\
     * This clears the entire {@link _texture texture} to black.
     */
    public clearTexture(): void {
        this._dataVersion++;
        this._cachedPixels = undefined;
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        Array.from({ length: this.size[2] }).forEach((_, z) => {
            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                this._texture,
                0,
                z
            );
            gl.clear(gl.COLOR_BUFFER_BIT);
        });
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /**
     * Method to clear a single slice of the {@link _texture texture} of this {@link SignedDistanceField}.\
     * @param contourSliceZ The index of the slice to clear.
     */
    public clearSlice(contourSliceZ: number): void {
        this._dataVersion++;
        this._cachedPixels = undefined;
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        gl.framebufferTextureLayer(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            this._texture,
            0,
            contourSliceZ
        );
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /**
     * Method to get all the slices of this {@link SignedDistanceField}.
     */
    public getAllSlices(): number[] {
        return Array.from({ length: this.size[2] }, (_, i) => i);
    }

    /**
     * Creates a new SignedDistanceField cropped to the content bounds plus an optional margin.
     *
     * This is useful for extracting the relevant portion of a large working SDF into a
     * tightly-bounded texture that conserves GPU memory.
     *
     * @param threshold Pixel value threshold for content detection (values > threshold are content)
     * @param marginPx Margin in pixels to add around the content bounds on all sides
     * @returns
     *   - `{ kind: 'cropped', sdf, offset }` when the bounds are tighter than the
     *     current grid: a fresh GPU texture is allocated and the in-bounds pixels
     *     are uploaded. Caller owns the new SDF.
     *   - `{ kind: 'tight' }` when the bounds (with margin) already cover the full
     *     grid. No allocation, no readback-then-reupload.
     *   - `{ kind: 'empty' }` when no voxel exceeds the threshold.
     */
    public cropToContentBounds(
        threshold: number = 0,
        marginPx: number = 0
    ):
        | { kind: 'cropped'; sdf: SignedDistanceField; offset: vec3 }
        | { kind: 'tight' }
        | { kind: 'empty' } {
        const bounds = this.getContentBounds(threshold);
        if (!bounds) {
            return { kind: 'empty' };
        }

        // Apply margin, clamping to valid bounds
        const minX = Math.max(0, bounds.min[0] - marginPx);
        const minY = Math.max(0, bounds.min[1] - marginPx);
        const minZ = Math.max(0, bounds.min[2] - marginPx);
        // Add 1 to max because bounds are inclusive indices
        const maxX = Math.min(this._size[0], bounds.max[0] + 1 + marginPx);
        const maxY = Math.min(this._size[1], bounds.max[1] + 1 + marginPx);
        const maxZ = Math.min(this._size[2], bounds.max[2] + 1 + marginPx);

        // Bounds (with margin) already cover the entire grid — skip the
        // alloc/copy/upload roundtrip entirely.
        if (
            minX === 0 &&
            minY === 0 &&
            minZ === 0 &&
            maxX === this._size[0] &&
            maxY === this._size[1] &&
            maxZ === this._size[2]
        ) {
            return { kind: 'tight' };
        }

        const croppedSize = vec3.fromValues(
            maxX - minX,
            maxY - minY,
            maxZ - minZ
        );

        // Create new SDF with cropped dimensions
        const croppedSdf = new SignedDistanceField(croppedSize);

        // Copy pixel data
        const sourcePixels = this.pixels;
        const croppedPixels: Uint8Array[] = [];

        for (let z = minZ; z < maxZ; z++) {
            const sourceSlice = sourcePixels[z];
            const croppedSlice = new Uint8Array(
                croppedSize[0] * croppedSize[1]
            );

            for (let y = 0; y < croppedSize[1]; y++) {
                const sourceY = y + minY;
                const sourceRowStart = sourceY * this._size[0] + minX;
                const destRowStart = y * croppedSize[0];

                // Copy row
                croppedSlice.set(
                    sourceSlice.subarray(
                        sourceRowStart,
                        sourceRowStart + croppedSize[0]
                    ),
                    destRowStart
                );
            }
            croppedPixels.push(croppedSlice);
        }

        // Upload to GPU
        croppedSdf.arrayToTexture(croppedPixels);

        return {
            kind: 'cropped',
            sdf: croppedSdf,
            offset: vec3.fromValues(minX, minY, minZ),
        };
    }

    /**
     * Copies a region from this SDF to a destination SDF.
     *
     * @param dest Destination SignedDistanceField
     * @param srcOffset Starting position in this SDF (source)
     * @param destOffset Starting position in destination SDF
     * @param size Size of region to copy
     */
    public copyRegionTo(
        dest: SignedDistanceField,
        srcOffset: ReadonlyVec3,
        destOffset: ReadonlyVec3,
        size: ReadonlyVec3
    ): void {
        // Compute effective X copy range, clamping negative offsets
        const xSkip = Math.max(0, -srcOffset[0], -destOffset[0]);
        const effectiveSrcX = srcOffset[0] + xSkip;
        const effectiveDestX = destOffset[0] + xSkip;
        const copyWidth = Math.min(
            size[0] - xSkip,
            this._size[0] - effectiveSrcX,
            dest.size[0] - effectiveDestX
        );

        if (copyWidth <= 0) {
            return;
        }

        const sourcePixels = this.pixels;
        const destPixels = dest.pixels;

        for (let z = 0; z < size[2]; z++) {
            const srcZ = srcOffset[2] + z;
            const destZ = destOffset[2] + z;

            if (srcZ < 0 || srcZ >= this._size[2]) {
                continue;
            }
            if (destZ < 0 || destZ >= dest.size[2]) {
                continue;
            }

            const sourceSlice = sourcePixels[srcZ];
            const destSlice = destPixels[destZ];

            for (let y = 0; y < size[1]; y++) {
                const srcY = srcOffset[1] + y;
                const destY = destOffset[1] + y;

                if (srcY < 0 || srcY >= this._size[1]) {
                    continue;
                }
                if (destY < 0 || destY >= dest.size[1]) {
                    continue;
                }

                const srcRowStart = srcY * this._size[0] + effectiveSrcX;
                const destRowStart = destY * dest.size[0] + effectiveDestX;

                destSlice.set(
                    sourceSlice.subarray(srcRowStart, srcRowStart + copyWidth),
                    destRowStart
                );
            }

            // Upload modified slice to GPU
            dest.arrayToTextureSlice(destSlice, destZ);
        }
    }
}
